import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns/format';
import { formatDistanceStrict } from 'date-fns/formatDistanceStrict';
import { PrimaryButton } from '../../components/buttons';
import { CompanionCard } from '../../components/companion-card';
import { DemoCTAModal, useDemoCTA } from '../../components/demo-cta-modal';
import { DownloadUploaderButton } from '../../components/download-uploader-button';
import { EmptyState } from '../../components/empty-state';
import { Loading } from '../../components/loading';
import { Row, Table } from '../../components/tables';
import { isFailedStatus, type Upload, UploadStatus, UploadTrigger } from '../../data/upload';
import type { UploaderConnection } from '../../data/uploader-connection';
import { useCompanionConnections, useTriggerUpload, useUploads } from '../../hooks/uploads';
import { storeLayout } from '../../router';

const STATUS_STYLES: Record<string, string> = {
  [UploadStatus.Completed]: 'bg-green-100 text-green-800',
  [UploadStatus.UploadFailed]: 'bg-red-100 text-red-800',
  [UploadStatus.ProcessingFailed]: 'bg-red-100 text-red-800',
  [UploadStatus.Processing]: 'bg-blue-100 text-blue-800',
  [UploadStatus.Uploading]: 'bg-blue-100 text-blue-800',
  [UploadStatus.Uploaded]: 'bg-blue-100 text-blue-800',
  [UploadStatus.Compressing]: 'bg-yellow-100 text-yellow-800',
  [UploadStatus.Finding]: 'bg-yellow-100 text-yellow-800',
  [UploadStatus.Pending]: 'bg-gray-100 text-gray-800',
};

const SOURCE_LABELS: Record<string, string> = {
  [UploadTrigger.Scheduled]: 'Scheduled',
  [UploadTrigger.Auto]: 'Scheduled',
  [UploadTrigger.Manual]: 'Manual (Companion)',
  [UploadTrigger.Remote]: 'Manual (Web)',
};

// Statuses before the data has been uploaded to S3.
const UPLOAD_PHASE_STATUSES = new Set<UploadStatus>([
  UploadStatus.Pending,
  UploadStatus.Finding,
  UploadStatus.Compressing,
  UploadStatus.Uploading,
]);

// One amber for every way an upload can stop without a verdict. They read as a
// group rather than as three unrelated states, and stay clear of the greens and
// reds that mean the upload actually reached one.
const STALLED_STYLE = 'bg-amber-100 text-amber-800';

// stalledPresentation says how to describe a record the server has marked
// stalled. The status still records how far it got, and that is what decides the
// wording: nobody answered the trigger, a Companion quit partway through, or the
// archive landed and the server-side work behind it was abandoned.
function stalledPresentation(status: UploadStatus): { label: string; detail: string } {
  if (status === UploadStatus.Pending) {
    return {
      label: 'not received',
      detail: 'No Companion picked this up. Trigger another upload.',
    };
  }

  if (UPLOAD_PHASE_STATUSES.has(status)) {
    return {
      label: 'interrupted',
      detail: 'The Companion stopped partway through. Trigger another upload.',
    };
  }

  return {
    label: "didn't finish",
    detail: 'This upload stopped before it finished. Trigger another upload.',
  };
}

function StatusBadge({ status, style }: { status: string; style?: string }) {
  const resolved = style ?? STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-800';

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${resolved}`}
    >
      {status}
    </span>
  );
}

const cellClassName = 'whitespace-nowrap px-1 py-2 text-sm text-gray-700 font-mono';

function UploadRow({ upload }: { upload: Upload }) {
  const now = new Date();
  const status = upload.status;
  const startedAt = new Date(upload.started_at);
  const updatedAt = new Date(upload.updated_at);
  const uploadedAt = upload.upload_finished_at ? new Date(upload.upload_finished_at) : null;
  const processingStartedAt = upload.processing_started_at
    ? new Date(upload.processing_started_at)
    : null;
  // When the worker stopped, falling back to the last write for records from
  // before the server recorded it. `updated_at` is only a stand-in: it means
  // "last write of any kind", so anything touching a finished record afterwards
  // stretches the duration — which is why the demo store, whose rows are
  // re-copied nightly, showed uploads that processed in seconds as taking days.
  const processingFinishedAt = upload.processing_finished_at
    ? new Date(upload.processing_finished_at)
    : updatedAt;

  // A record the server has called stalled is done moving, whatever phase its
  // status records. Every elapsed-time branch below has to stop counting at that
  // point: measured against `now` and recomputed each render, they had no upper
  // bound, so an upload nothing was working on displayed as one that had been
  // running for hours — or, past the upload phase, for its whole 90-day life.
  const stalled = upload.is_stalled ? stalledPresentation(status) : null;

  // Upload time: from start until data lands in S3.
  // Shows elapsed while in the upload phase, fixed once uploaded.
  let uploadTime: string | null = null;
  if (stalled && UPLOAD_PHASE_STATUSES.has(status)) {
    // A trigger nobody answered ran nothing, so it has no duration to report — a
    // number there would be time spent waiting, not working. One that died
    // partway did run, and for exactly this long: freeze it at the last report
    // rather than counting on against a Companion that is not coming back.
    uploadTime =
      status === UploadStatus.Pending ? null : formatDistanceStrict(startedAt, updatedAt);
  } else if (UPLOAD_PHASE_STATUSES.has(status)) {
    uploadTime = formatDistanceStrict(startedAt, now);
  } else if (uploadedAt) {
    uploadTime = formatDistanceStrict(startedAt, uploadedAt);
  }

  // Processing time: from when the server started processing until it finished.
  // Measured from processing_started_at so re-processing a file gives accurate results.
  let processingTime: string | null = null;
  if (processingStartedAt) {
    if (status === UploadStatus.Processing) {
      // Frozen at the last write once stalled: a worker that stopped 19 days ago
      // did not spend 19 days processing.
      processingTime = formatDistanceStrict(processingStartedAt, stalled ? updatedAt : now);
    } else if (status === UploadStatus.Completed || status === UploadStatus.ProcessingFailed) {
      processingTime = formatDistanceStrict(processingStartedAt, processingFinishedAt);
    }
  }

  let details: React.ReactNode = null;
  if (stalled) {
    details = <span className="text-amber-700">{stalled.detail}</span>;
  } else if (isFailedStatus(status) && upload.error_message) {
    details = <span className="text-red-600">{upload.error_message}</span>;
  }

  return (
    <Row>
      <td className={cellClassName}>
        <StatusBadge
          status={stalled ? stalled.label : upload.status}
          style={stalled ? STALLED_STYLE : undefined}
        />
      </td>
      <td className={cellClassName}>{SOURCE_LABELS[upload.trigger] ?? upload.trigger}</td>
      <td className={cellClassName}>{format(startedAt, 'MM/dd/yyyy hh:mm aa')}</td>
      <td className={cellClassName}>{uploadTime}</td>
      <td className={cellClassName}>{processingTime}</td>
      {/* Free text, unlike every other cell — it has to wrap or it stretches the table. */}
      <td className={`${cellClassName} max-w-xs whitespace-normal`}>{details}</td>
    </Row>
  );
}

function ConnectedUploaders({
  connections,
  activeMachineIds,
}: {
  connections: UploaderConnection[];
  activeMachineIds: Set<string | undefined>;
}) {
  return (
    <div className="mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-3">Connected Companions</h2>
      {connections.length === 0 ? (
        <p className="text-sm text-gray-500">No companions online</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {connections.map((conn) => (
            <CompanionCard
              key={conn.connection_id}
              connection={conn}
              isActive={activeMachineIds.has(conn.uploader?.machine_id ?? '')}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const HEADINGS = ['Status', 'Source', 'Started', 'Upload', 'Processing', 'Details'];

export function Uploads() {
  const { store } = storeLayout.useRouteContext();
  const { isDemo, showCTA, ctaProps } = useDemoCTA(store);
  const { uploads, loading } = useUploads();
  const { connections, loading: connectionsLoading } = useCompanionConnections();
  const { triggerUpload, isPending } = useTriggerUpload();

  const hasUploaders = connections.length > 0;

  const onlineMachineIds = new Set(connections.map((c) => c.uploader?.machine_id).filter(Boolean));

  // An upload only blocks a new one if the server still holds the lease AND the
  // companion running it is still connected. A companion that vanished mid-upload
  // drops out of `connections` within seconds (companion_status_changed push), so
  // the button recovers without waiting for the lease to lapse.
  const isBlocking = (u: Upload) => (u.is_active ?? false) && onlineMachineIds.has(u.machine_id);

  const hasActiveUpload = uploads.some(isBlocking);
  const activeMachineIds = new Set(uploads.filter(isBlocking).map((u) => u.machine_id));

  if (loading || connectionsLoading) {
    return <Loading />;
  }

  return (
    <>
      <DemoCTAModal {...ctaProps} />
      <div className="h-full w-full flex flex-col">
        <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <h1 className="text-3xl font-semibold text-gray-900">Upload History</h1>
            <PrimaryButton
              disabled={!hasUploaders || isPending || hasActiveUpload}
              onClick={() => (isDemo ? showCTA() : triggerUpload())}
              title={
                !hasUploaders
                  ? 'No companions online'
                  : hasActiveUpload
                    ? 'An upload is in progress'
                    : 'Trigger a remote upload'
              }
            >
              {isPending ? 'Triggering...' : 'Trigger Upload'}
            </PrimaryButton>
          </div>
          <ConnectedUploaders connections={connections} activeMachineIds={activeMachineIds} />
          {uploads.length === 0 ? (
            <EmptyState
              icon={<CloudArrowUpIcon className="size-8" />}
              title="No uploads yet."
              description={
                hasUploaders
                  ? 'Trigger an upload above, or wait for the next scheduled sync.'
                  : 'Install the uploader to start syncing your LiquorPOS data.'
              }
              action={hasUploaders ? undefined : <DownloadUploaderButton primary />}
            />
          ) : (
            <Table headings={HEADINGS}>
              {uploads.map((upload) => (
                <UploadRow key={upload.upload_id} upload={upload} />
              ))}
            </Table>
          )}
        </div>
      </div>
    </>
  );
}
